import classnames from 'classnames';
import moment from 'moment';
import React from 'react';

import Modal from 'app/components/Modal/Modal';
import {useUpcomingInvoices} from 'app/providers/ImageryContractProvider';
import * as CONSTANTS from 'app/utils/constants';
import * as costUtils from 'app/utils/costUtils';

import cs from './ContractHistory.module.scss';
import {formatInvoiceDueDate, formatLineItem} from './utils';

export const ImageryPaymentSection: React.FunctionComponent<{
  organizationId: string;
  isLoadingContracts: boolean;
  imagerySubscriptionIds: string[] | undefined;
  paysAtCheckout: boolean;
}> = ({organizationId, isLoadingContracts, imagerySubscriptionIds, paysAtCheckout}) => {
  const [upcomingInvoicesStatusMaybe] = useUpcomingInvoices(organizationId, imagerySubscriptionIds);
  // Orders charge a card at checkout, so only invoices still owed from before the org switched
  // are worth showing. Stripe previews a $0 invoice for any live metered subscription.
  const upcomingInvoices = paysAtCheckout
    ? upcomingInvoicesStatusMaybe.value?.filter((invoice) => invoice['amount_due'] > 0)
    : upcomingInvoicesStatusMaybe.value;
  const isLoading = isLoadingContracts || upcomingInvoicesStatusMaybe.status === 'unknown';
  const [lineItemModalOpen, setLineItemModalOpen] = React.useState(false);

  if (paysAtCheckout && !upcomingInvoices?.length) {
    return null;
  }

  const parseNextPayment = (invoice) => {
    if (!invoice) {
      return {parsedDueDate: null, parsedAmountDue: 0};
    }
    const amountDue = costUtils.formatCost(invoice['amount_due']);
    return {
      parsedDueDate: formatInvoiceDueDate(invoice),
      parsedAmountDue: amountDue,
    };
  };

  const formattedDueDates = (invoices) => {
    const uniqueDueDates: string[] = Array.from(
      new Set(invoices.map((invoice) => parseNextPayment(invoice).parsedDueDate))
    );
    return uniqueDueDates.join(' and ');
  };

  return (
    <div className={cs.planBillingCardSection}>
      <div className={cs.sectionLabel}>
        Imagery payment{(upcomingInvoices?.length ?? 0) > 1 && 's'}
      </div>
      {!upcomingInvoices?.length ? (
        isLoading ? (
          <>
            <div className={classnames(cs.sectionValue, 'bp5-skeleton')}>$0.00</div>
            <div className={classnames(cs.sectionSub, 'bp5-skeleton')}>Due MMM D</div>
            <a className="bp5-skeleton">View details</a>
          </>
        ) : (
          <div className={cs.sectionSub}>
            No invoice found. Please reach out to{' '}
            <a href="mailto:lens@upstream.tech">lens@upstream.tech</a> with any questions.
          </div>
        )
      ) : (
        <>
          <div className={cs.sectionValue}>
            {upcomingInvoices.length === 1
              ? parseNextPayment(upcomingInvoices[0]).parsedAmountDue
              : costUtils.formatCost(
                  upcomingInvoices.reduce((acc, invoice) => (acc += invoice['amount_due']), 0)
                )}
          </div>
          <div className={cs.sectionSub}>
            {upcomingInvoices.length === 1
              ? `Due ${parseNextPayment(upcomingInvoices[0]).parsedDueDate}`
              : `Due ${formattedDueDates(upcomingInvoices)}`}
          </div>
          {/*
              Uses 'amount_due' rather than 'total', unlike SubscriptionPaymentSection.
              We want to show imagery invoices with account credit applied.
          */}
          {Math.abs(upcomingInvoices[0]['ending_balance']) > 0 && (
            <div className={cs.sectionSub}>
              Remaining credit:{' '}
              {costUtils.formatCost(Math.abs(upcomingInvoices[0]['ending_balance']))}
              <br />
              Last synced:{' '}
              {moment(
                upcomingInvoices[0].parent?.subscription_details?.metadata['last_synced_at']
              ).format('h:mma, M/DD/YYYY')}
            </div>
          )}
          <a onClick={() => setLineItemModalOpen(true)}>View details</a>
          <Modal
            isOpen={lineItemModalOpen}
            className={cs.invoicesModalContent}
            onClose={() => setLineItemModalOpen(false)}
            headerText={`Next imagery payment${upcomingInvoices.length > 1 ? 's' : ''}`}
            hideActions
          >
            {upcomingInvoices.map((invoice, idx) => (
              <div key={idx}>
                <h3>
                  {upcomingInvoices.length > 1 && `${idx + 1}. `}Invoice due{' '}
                  {parseNextPayment(invoice).parsedDueDate}
                </h3>
                <ul>
                  {invoice['lines']['data']
                    .filter((d) => {
                      const product = d.pricing?.price_details?.product;
                      return (
                        !product ||
                        ![
                          CONSTANTS.LIVE_LENS_PUBLIC_IMAGERY_PRODUCT_ID,
                          CONSTANTS.TEST_LENS_PUBLIC_IMAGERY_PRODUCT_ID,
                        ].includes(product)
                      );
                    })
                    .map((d) => formatLineItem(d))}
                  {invoice['starting_balance'] !== 0 && (
                    <>
                      <li>
                        <span>Current account credit</span>
                        <span>({costUtils.formatCost(Math.abs(invoice['starting_balance']))})</span>
                      </li>
                      <li>
                        <span>Remaining account credit after this invoice</span>
                        <span>({costUtils.formatCost(Math.abs(invoice['ending_balance']))})</span>
                      </li>
                    </>
                  )}
                  <li>
                    <div>Total due</div>
                    <div>{parseNextPayment(invoice).parsedAmountDue}</div>
                  </li>
                </ul>
              </div>
            ))}
          </Modal>
        </>
      )}
    </div>
  );
};
